import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  output,
  Output,
  viewChildren,
} from '@angular/core';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { refreshOnDisplayZoneChange } from '@modules/full-calendar/types/display-zone-refresh';
import { CalendarType } from '@modules/calendar-app/types/calendar-type';
import { type DragData } from '@modules/drag-n-drop/types/drag-data';
import { type CalendarCellClickEvent } from '@modules/full-calendar/types/calendar-cell-click-event';
import { type CalendarDropEvent } from '@modules/full-calendar/types/calendar-drop-event';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';
import { beginningOfDay, endOfDay } from '@sharedlib/common/utils/date';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { Subject } from 'rxjs';
import { CalendarMonthSmallComponent } from '../calendar-month-small/calendar-month-small.component';
import { groupEventsByMonthInZone, type MonthBucket } from './calendar-year-months.util';

@Component({
  selector: 'stch-calendar-year',
  templateUrl: './calendar-year.component.html',
  styleUrls: ['./calendar-year.component.less'],
  standalone: false,
})
export class FullCalendarYearComponent {
  public readonly events = input.required<CalendarEventSummaryDto[]>();
  public readonly viewDate = input.required<Date>();
  public readonly scrollToDate = input<Date>();
  /**
   * The day to mark, already projected into the display frame (see full-calendar). Compared with
   * `sameDisplayDay`, which re-anchors each cell's account-midnight instant into the same frame —
   * a browser-local `sameDay` would ring the neighbouring cell when account zone != device zone.
   */
  public readonly selectedDate = input<Date | null>();

  @Output() dateClicked = new EventEmitter<CalendarCellClickEvent>();
  @Output() dateDblClicked = new EventEmitter<Date>();
  @Output() eventDropped = new EventEmitter<CalendarDropEvent>();
  protected readonly eventClicked = output<CalendarEventSummaryDto>();
  protected readonly eventDblClicked = output<CalendarEventSummaryDto>();

  protected readonly monthCalendars = viewChildren(CalendarMonthSmallComponent);

  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /** The zone cells are laid out in; the selected-day comparison must use it too. */
  protected get displayTimeZone(): string {
    return this.displayTimeZoneService.timeZone();
  }

  /** The year being displayed, read in the display zone so it agrees with the months laid out below. */
  protected readonly year = computed(() =>
    toDisplayZoneWallClock(this.viewDate() || new Date(), this.displayTimeZoneService.timeZone()).getFullYear(),
  );

  protected readonly eventsByMonth = computed<MonthBucket<CalendarEventSummaryDto>[]>(() => {
    // All-day events are positioned in the display zone (SBUX-829 H1); reactive to zone changes.
    const displayTimeZone = this.displayTimeZoneService.timeZone();
    const calendarEvents = this.events().map((event) => CalendarEvent.makeAngularCalendarEvent(event, displayTimeZone));

    return groupEventsByMonthInZone(this.year(), calendarEvents, displayTimeZone);
  });

  private readonly _ef1 = effect(() => {
    this.eventsByMonth(); // track changes
    this.refreshCalendar.next();
  });

  // Also re-lays every mini month out when the display zone arrives (see the helper's TSDoc).
  public refreshCalendar = refreshOnDisplayZoneChange(new Subject<void>());
  public contextMenuOpened: boolean;
  public readonly CalendarType = CalendarType;

  private readonly scrollToDateEffect = effect(() => {
    const scrollToDate = this.scrollToDate();
    setTimeout(() => this.scrollToDateHandler(scrollToDate));
  });

  // SBUX-829 C-1: emit the DISPLAY (account) zone wall clock, not the raw account-zone-midnight
  // instant, so the all-day write path's browser-local reads reconstruct the intended account day
  // (see calendar-month.component). Otherwise clicking/dropping shifts a day when account ≠ browser.
  private toDisplayZoneDay(date: Date): Date {
    return toDisplayZoneWallClock(date, this.displayTimeZoneService.timeZone());
  }

  public clickDay(event: MouseEvent, date: Date, origin: ElementRef) {
    this.dateClicked.emit({ date: this.toDisplayZoneDay(date), originRef: origin, event });
  }

  public dblClickDay(date: Date) {
    this.dateDblClicked.emit(this.toDisplayZoneDay(date));
  }

  public handleDrop(dragData: DragData, day: Date, origin: HTMLElement) {
    const localDay = this.toDisplayZoneDay(day);
    this.eventDropped.emit({
      dragData,
      newStart: beginningOfDay(localDay),
      newEnd: endOfDay(localDay),
      originRef: new ElementRef(origin),
    });
  }

  public scrollToDateHandler(date: Date): boolean {
    if (!date) {
      return false;
    }

    return this.monthCalendars().some((calendar) => calendar.scrollToDateHandler(date));
  }
}
